
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";

  import LIST_DOCUMENTS from "@/apollo/documents/ListDocuments.gql";

  import CardPagination from "@/components/card/CardPagination.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholderNew.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import Search from "@/components/common/SearchNew.vue";
  import DocumentItem from "@/components/documents/DocumentItem.vue";
  import ActionsPanelWithFilters from "@/components/common/ActionsPanelWithFilters.vue";

import SearchableListConfig from "@/entities/SearchableListConfig";
import { Document, User } from "@/generated/graphql";
import searchableListConfig from "@/data/searchableListConfig";
import EmptyListImageDocuments from "@/components/icons/emptyListImages/EmptyListImageDocuments.vue";

import DocumentTable from "@/components/documents/DocumentTableResources.vue";

  interface SearchQuery {
    filename: { _ilike: string };
  }

@Component({
  components: {
    EmptyListImageDocuments,
    CardPagination,
    EmptyListPlaceholder,
    LoadingOverlay,
    Search,
    DocumentItem,
    ActionsPanelWithFilters,
    DocumentTable
  },
  apollo: {
    documents: {
      query: LIST_DOCUMENTS,
      variables() {
        return this.contractorDocumentArgs;
      },
      update(data) {
        this.searchableListConfig.total = data.total.aggregate.count;
        this.searchableListConfig.loadedCount = data.document.length;
        return data.document;
      },
    },
  },
})
export default class SearchableDocumentsListCustom extends Vue {
  @Prop({ type: String, default: undefined }) readonly userFullName?: string;
  @Prop({ type: String, default: undefined })
  readonly attachedToUserId?: string;
  @Prop({ type: String, default: undefined }) readonly projectId?: string;
  @Prop({ type: Object, default: () => ({}) }) readonly query!: Object;
  @Prop({ type: Boolean, default: false }) readonly frozen!: boolean;
  @Prop({ type: String, default: "" }) readonly searchByDocumentName!: string;
  @Prop({ type: Boolean, default: false })
  readonly outerFilterTouched!: boolean;
  @Prop({ type: Boolean, default: false }) readonly extraInfoHidden!: boolean;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, default: undefined }) readonly customRequestId?: string;
  @Prop({type:String,default:undefined}) readonly companyId?:string;

    documents: Document[] | undefined = undefined;
    searchTouched: boolean = false;
    searchableListConfig: SearchableListConfig = { ...searchableListConfig };

    get search(): string {
      return this.searchableListConfig.search || this.searchByDocumentName;
    }

    get isEmptyListActionsPassed(): boolean {
      return Boolean(this.$scopedSlots.emptyListActions);
    }

    get emptyListTitle(): string {
      if (!this.isEmptyListActionsPassed) {
        return "No available documents";
      }

      if (this.userFullName) {
        return ``;
      }
      
      return "Start adding your documents";
    }

    get emptyListSubtitle(): string {
      if (this.frozen) {
        return "No available documents";
      }

      if (this.userFullName) {
        return `Start adding documents to ${this.userFullName} document list`;
      }

      return "";
    }

    get contractorDocumentArgs(): any {
      const { page, limit } = this.searchableListConfig;
      const nextOffset = page * limit - limit;
      const params = {
        offset: nextOffset,
        limit: limit,
      };
      const searchQuery: SearchQuery = {
        filename: { _ilike: `%${this.search}%` },
      };

      return {
        where: {
          ...searchQuery,
          ...this.query,
        },
      };
    }

    get isDocumentsPresent(): boolean {
      if (this.documents) {
        return this.documents.length > 0;
      }
      return false;
    }

    get isDocumentsExist(): boolean {
      return this.searchTouched || this.outerFilterTouched || this.isDocumentsPresent;
    }

    get isSearchResultEmpty(): boolean {
      return !this.isDocumentsPresent && (this.searchTouched || this.outerFilterTouched);
    }

    get isEmptyListPlaceholderShown(): boolean {
      return !this.isDocumentsExist && !this.$apollo.loading;
    }

    @Watch("searchByDocumentName")
    onOutsideSearchChange() {
      if (!this.searchTouched) {
        this.searchTouched = true;
      }
    }

    isFrozen(document: Document): boolean {
      if (this.frozen) {
        return true;
      }

      if (this.currentUser.role === "ADMIN") {
        return false;
      }

      if (this.customRequestId) {
        return false;
      }

      if(!this.currentUser) {
        return false;
      }

      return this.currentUser.id !== document.uploaded_by;
    }

    onSearch(value: string): void {
      if (!this.searchTouched) {
        this.searchTouched = true;
      }
      this.searchableListConfig.search = value;
    }

    async updateDocumentList(): Promise<void>{
      this.$emit("listChanged");
      await this.$apollo.queries.documents.refetch();
      this.$emit("documentNumberChanged", this.searchableListConfig.total)
    }
  }
